import {TargetBlank} from '@wandb/weave/common/util/links';
import {Switch} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import Color from 'color';
import * as React from 'react';
import {
  Accordion,
  Button,
  Dropdown,
  Icon,
  Input,
  TextArea,
} from 'semantic-ui-react';

import {getTextColor} from '../util/colors';
import DOC_URLS from '../util/doc_urls';
import {getLegendOverrideKey} from '../util/plotHelpers/legend';
import {
  type Line,
  type Mark,
  type RunDataSeries,
} from '../util/plotHelpers/types';
import {type Query} from '../util/queryTypes';
import {useDebouncedValue} from '../util/useDebouncedValue';
import ColorPicker from './ColorPicker';
import {LabeledOptionBlock as LabeledOption} from './elements/LabeledOptionBlock';
import LineStylePicker from './LineStylePicker';
import {useSingleRunModeContext} from './MultiRunWorkspace/SingleRunMode';
import PanelChartOptions from './PanelChartOptions';
import {usePanelConfigContext} from './PanelRunsLinePlot/PanelConfigContext';
import {type RunsLinePlotConfig} from './PanelRunsLinePlot/types';
import {useColorByMetrics} from './PanelRunsLinePlot/useColorByMetrics';
import {useLegendVisibility} from './PanelRunsLinePlot/useLegendVisibility';
import ProjectFieldSelector from './ProjectFieldSelector';

type PanelConfig = {
  forceReactVis?: boolean;
  metrics?: string[];
  legendFields?: string[];
  legendTemplate?: string;
  colorEachMetricDifferently?: boolean;
  overrideSeriesTitles?: {[key: string]: string};
  overrideColors?: {[key: string]: {color: string; transparentColor: string}};
  overrideMarks?: {[key: string]: Mark};
};

type PanelDisplayPreferencesProps = {
  type: 'lines' | 'bars';
  config: PanelConfig;
  updateConfig: (newConfig: PanelConfig) => void;
  defaultTitle?: string;
  defaultXAxisTitle?: string;
  defaultYAxisTitle?: string;
  pageQuery: Query; // for the field selector
  defaultLegendTemplate?: string;
  editableLegendSeries: RunDataSeries[];
  lines?: Line[];
};

// Hoisted to stable references so the memoized ProjectFieldSelector does not
// re-render on prop identity churn every parent render.
const LEGEND_FIELD_TYPES = ['string', 'number', 'boolean'];
const DEFAULT_LEGEND_KEYS = [
  'run:displayName',
  'run:name',
  'run:createdAt',
  'run:userName',
];
const DEFAULT_LEGEND_FIELDS = ['run:displayName'];

const PanelDisplayPreferences: React.FC<PanelDisplayPreferencesProps> =
  React.memo(
    ({
      pageQuery,
      defaultLegendTemplate,
      type,
      editableLegendSeries,
      config,
      updateConfig,
      lines,
    }) => {
      const [advancedLegendVisible, setAdvancedLegendVisible] =
        React.useState(false);
      const {isMultiMetric, showLegend} = usePanelConfigContext();
      const isLegendShown = useLegendVisibility(showLegend, isMultiMetric);
      const colorEachMetricDifferently = useColorByMetrics(config);

      const updateConfigWithLegendTemplate = React.useCallback(
        (v: string) => {
          updateConfig({legendTemplate: v});
        },
        [updateConfig]
      );
      const setLegendFields = React.useCallback(
        (value: string[]) => {
          updateConfig({legendFields: value, legendTemplate: undefined});
        },
        [updateConfig]
      );
      const {
        value: template,
        debouncedSetValue: debouncedSetTemplate,
        flush,
      } = useDebouncedValue<string>(
        config.legendTemplate ?? defaultLegendTemplate ?? '',
        350,
        updateConfigWithLegendTemplate
      );

      return (
        <>
          <PanelChartOptions
            config={config}
            pageQuery={pageQuery}
            showLegend={isLegendShown}
            type={type}
            updateConfig={updateConfig}
            lines={lines}
          />
          <LabeledOption
            label="Legend"
            labelWidth="140px"
            helpText="Set variables in the chart legend"
            docUrl={DOC_URLS.compareMetrics + '#legend'}
            option={
              <ProjectFieldSelector
                className="legend"
                disabled={!config.metrics || config.metrics.length === 0}
                query={pageQuery}
                types={LEGEND_FIELD_TYPES}
                defaultKeys={DEFAULT_LEGEND_KEYS}
                selection
                fluid
                multi
                value={config.legendFields || DEFAULT_LEGEND_FIELDS}
                searchByKeyAndText
                setValue={setLegendFields}
              />
            }
          />
          {isMultiMetric && (
            <LabeledOption
              label="Color metrics"
              helpText={`Color ${type} by metrics instead of runs`}
              labelWidth="140px"
              option={
                <Tailwind>
                  <Switch.Root
                    size="medium"
                    checked={colorEachMetricDifferently}
                    onCheckedChange={isChecked =>
                      updateConfig({colorEachMetricDifferently: isChecked})
                    }>
                    <Switch.Thumb
                      size="medium"
                      checked={colorEachMetricDifferently}
                    />
                  </Switch.Root>
                </Tailwind>
              }
            />
          )}
          <Accordion className="advanced-options">
            <Accordion.Title
              active={advancedLegendVisible}
              onClick={() => {
                setAdvancedLegendVisible(cur => !cur);
              }}>
              <Icon name="dropdown" />
              Advanced Legend
            </Accordion.Title>
            <Accordion.Content active={advancedLegendVisible}>
              <p className="advanced-legend-info chart-label">
                {`Edit the legend template directly. For example:`}
                <br />
                {`\${config:epochs} displays the config value epochs for each line.`}
                <br />
                {`\${summary:loss} displays the summary value loss for each line.`}
                <br />
                {`[[ $x: $y ]] to displays history values in the tooltip hovering over a line.`}
                <br />

                <TargetBlank href={DOC_URLS.compareMetrics + '#legend'}>
                  See the docs →
                </TargetBlank>
              </p>

              <TextArea
                value={template}
                className="chart-legend-template"
                onChange={(e, {value}) =>
                  debouncedSetTemplate(String(value ?? ''))
                }
                onBlur={flush}
              />
            </Accordion.Content>
          </Accordion>

          <OverrideDataSeriesConfig
            editColor
            editMark={type === 'lines'}
            editLineWidths={type === 'lines'}
            config={config}
            updateConfig={updateConfig}
            editableLegendSeries={editableLegendSeries}
          />
        </>
      );
    }
  );

export default PanelDisplayPreferences;

function getLegendDisplayName(line: RunDataSeries): string {
  if (!line.displayName) {
    return '';
  }
  return `${line.displayName} ${line.metricName ?? ''}`;
}

type OverrideDataSeriesConfigProps = {
  config: RunsLinePlotConfig;
  updateConfig: (newConfig: RunsLinePlotConfig) => void;
  editableLegendSeries: RunDataSeries[];
  editColor: boolean;
  editMark: boolean;
  editLineWidths: boolean;
};
const OverrideDataSeriesConfig: React.FC<OverrideDataSeriesConfigProps> =
  React.memo(
    ({
      config,
      updateConfig,
      editColor,
      editMark,
      editLineWidths,
      editableLegendSeries,
    }) => {
      const {isSingleRun} = useSingleRunModeContext();

      if (editableLegendSeries.length === 0) {
        return <p>No editable lines on this chart.</p>;
      }
      const thicknessOptions = [0.5, 1, 1.5, 2, 3].map(val => ({
        key: val,
        text: `${val}px`,
        value: val,
      }));

      return (
        <div className="legend-options">
          {editableLegendSeries.map((l, i) => {
            const lineKey = getLegendOverrideKey(l, !isSingleRun);
            const lineKeyDisplayName = getLegendDisplayName(l);
            const usingOverride =
              config.overrideSeriesTitles?.[lineKey] != null ||
              config.overrideColors?.[lineKey] != null ||
              config.overrideMarks?.[lineKey] != null;

            const runColor = config.overrideColors?.[lineKey]?.color ?? l.color;

            return (
              // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
              <div key={i} className="legend-option">
                <div className="series-name">
                  <p
                    className="series-name-value"
                    style={{
                      color: getTextColor(runColor),
                    }}>
                    {lineKeyDisplayName}
                  </p>
                </div>
                <div className="override-name">
                  {editColor && (
                    <ColorPicker
                      activeColor={runColor || '#999999'}
                      // With current optimizations, some components do not update on function prop changes.
                      // To avoid a stale callback, we include an empty style prop to force a re-render.
                      // (This is a workaround, not the ideal solution.)
                      // Refer to shouldUpdate.ts : L16
                      // oxlint-disable-next-line wandb/jsx-no-new-object-as-prop -- intentional fresh object each render: forces the memoized ColorPicker to re-render so setColor isn't stale (see comment above)
                      style={{}}
                      // oxlint-disable-next-line wandb/jsx-no-new-function-as-prop -- inside .map (no hooks) and ColorPicker re-renders by design above; stabilizing has no effect here
                      setColor={value => {
                        const newOverrideColors = {...config.overrideColors};
                        const colors = {
                          color: value,
                          transparentColor: Color(value).alpha(0.1).toString(),
                        };

                        newOverrideColors[lineKey] = colors;
                        updateConfig({
                          overrideColors: newOverrideColors,
                        });
                      }}
                    />
                  )}
                  {editMark && (
                    <LineStylePicker
                      activeLineStyle={
                        config.overrideMarks?.[lineKey] ||
                        editableLegendSeries[i].mark ||
                        'solid'
                      }
                      // oxlint-disable-next-line wandb/jsx-no-new-function-as-prop -- LineStylePicker not React.memo'd and handler is in .map (no hooks); to optimize: memoize the child + lift the row handler
                      setLineStyle={(mark: Mark) => {
                        const newOverrideMarks = {...config.overrideMarks};
                        newOverrideMarks[lineKey] = mark;
                        updateConfig({
                          overrideMarks: newOverrideMarks,
                        });
                      }}
                    />
                  )}
                  {editLineWidths && (
                    <Dropdown
                      className="line-width-picker"
                      defaultValue={config.overrideLineWidths?.[lineKey] || 1}
                      selection
                      options={thicknessOptions}
                      onChange={(e, {value}) => {
                        const newOverrideLineWidths = {
                          ...config.overrideLineWidths,
                        };

                        newOverrideLineWidths[lineKey] = Number.parseFloat(
                          (value ?? 1).toString()
                        );
                        updateConfig({
                          overrideLineWidths: newOverrideLineWidths,
                        });
                      }}
                    />
                  )}
                  <Input
                    className="override-value"
                    defaultValue={l.title?.toString()}
                    onChange={(e, {value}) => {
                      const newOverrideLineTitles = {
                        ...config.overrideSeriesTitles,
                      };

                      newOverrideLineTitles[lineKey] = value;
                      updateConfig({
                        overrideSeriesTitles: newOverrideLineTitles,
                      });
                    }}
                  />
                  <Button
                    icon
                    disabled={!usingOverride}
                    onClick={() => {
                      const newOverrideSeriesTitles = {
                        ...config.overrideSeriesTitles,
                      };
                      delete newOverrideSeriesTitles[lineKey];

                      const newOverrideColors = {...config.overrideColors};
                      delete newOverrideColors[lineKey];

                      const newOverrideMarks = {...config.overrideMarks};
                      delete newOverrideMarks[lineKey];

                      updateConfig({
                        overrideSeriesTitles: newOverrideSeriesTitles,
                        overrideColors: newOverrideColors,
                        overrideMarks: newOverrideMarks,
                      });
                    }}>
                    <Icon name="sync" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  );
